/**
 * WBS 상태·진척 공유 계산 (ISS2026100053)
 * 간트(WbsGantt)·목록(ProjectList)이 같은 로직을 쓰도록 분리.
 * 상태 매트릭스: 준비/지연(착수·구축)/진행/완료, 상위=가중평균+우선순위.
 */

export type Act = {
  id: string;
  name: string;
  ps: string; // 계획시작 YYYYMMDD
  pe: string; // 계획종료
  as?: string; // 실적시작
  ae?: string; // 실적종료
  weight: number; // rt_weight
  children?: Act[];
};
export type Project = { id: string; name: string; pm: string; acts: Act[] };

// ── 날짜 유틸 (YYYYMMDD 문자열) ──
export const toDate = (s: string) =>
  new Date(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8));
export const pad = (n: number) => String(n).padStart(2, "0");
export const fmt = (d: Date) =>
  `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
export const diffDays = (a: string, b: string) =>
  Math.round((toDate(b).getTime() - toDate(a).getTime()) / 86400000);
export const addDays = (s: string, n: number) =>
  fmt(new Date(toDate(s).getTime() + n * 86400000));
export const todayStr = () => fmt(new Date());

// ── 색상 ──
export const STATUS_COLOR: Record<string, string> = {
  준비: "#9ca3af",
  진행: "#2563eb",
  지연: "#dc2626",
  완료: "#16a34a",
};
const PRIORITY = ["지연", "진행", "준비", "완료"];

// ── 상태 계산 ──
export type St = { label: string; lateStart: boolean; lateEnd: boolean };
// 기준일 시점의 실적 — 기준일 이후 실적시작/종료는 아직 일어나지 않은 것으로 본다
export const asOf = (s: string | undefined, e: string | undefined, base: string) => {
  const as = s && s <= base ? s : undefined;
  return { as, ae: as && e && e <= base ? e : undefined };
};

export function calcStatus(ps: string, pe: string, s0: string | undefined, e0: string | undefined, base: string): St {
  const { as: s, ae: e } = asOf(s0, e0, base);
  if (!ps || !pe) return { label: "준비", lateStart: false, lateEnd: false }; // 계획일자 없음
  if (e) return { label: "완료", lateStart: !!s && s > ps, lateEnd: e > pe };
  if (!s)
    return base > ps
      ? { label: "지연", lateStart: true, lateEnd: false } // 착수지연(미착수)
      : { label: "준비", lateStart: false, lateEnd: false };
  const lateStart = s > ps;
  if (base > pe) return { label: "지연", lateStart, lateEnd: true }; // 구축지연
  return { label: "진행", lateStart, lateEnd: false };
}
export const aggStatus = (labels: string[]) => {
  const uniq = Array.from(new Set(labels));
  if (uniq.length <= 1) return uniq[0] || "준비";
  return PRIORITY.find((p) => uniq.includes(p)) || uniq[0];
};

// ── 지표 ──
export const planPct = (ps: string, pe: string, base: string) => {
  if (!ps || !pe) return 0;
  if (base < ps) return 0;
  if (base >= pe) return 100;
  return Math.round(((diffDays(ps, base) + 1) / (diffDays(ps, pe) + 1)) * 100);
};
const r1 = (n: number) => Math.round(n * 10) / 10;

/**
 * 계획/실적 = 가중치 점수. (예: 대표ACT 가중치 10 → 하위 세부ACT 가중치 합 10, 모두 끝나면 10)
 *  - 세부: 계획 = 가중치 × 계획경과율, 실적 = 기준일까지 실적종료됐으면 가중치(진행 중은 0)
 *  - 상위: 하위 점수 합을 상위 가중치 기준으로 환산(하위 합 ≠ 상위 가중치인 기존 데이터 보정)
 *  - 프로젝트: 대표ACT 점수 합(가중치 합 100 → %)
 */
export function rollup(a: Act, base: string): { plan: number; act: number; status: string } {
  if (a.children && a.children.length) {
    const kids = a.children.map((c) => ({ c, r: rollup(c, base) }));
    const kw = kids.reduce((s, k) => s + k.c.weight, 0);
    const scale = a.weight > 0 && kw > 0 ? a.weight / kw : 1;
    return {
      plan: r1(kids.reduce((s, k) => s + k.r.plan, 0) * scale),
      act: r1(kids.reduce((s, k) => s + k.r.act, 0) * scale),
      status: aggStatus(kids.map((k) => k.r.status)),
    };
  }
  return {
    plan: r1((a.weight * planPct(a.ps, a.pe, base)) / 100),
    act: asOf(a.as, a.ae, base).ae ? a.weight : 0,
    status: calcStatus(a.ps, a.pe, a.as, a.ae, base).label,
  };
}

// 프로젝트 집계(목록/간트 공용)
export function projectAgg(p: Project, base: string) {
  const rs = p.acts.map((a) => rollup(a, base));
  const plan = r1(rs.reduce((s, r) => s + r.plan, 0));
  const act = r1(rs.reduce((s, r) => s + r.act, 0));
  const status = aggStatus(rs.map((r) => r.status));
  const ps = p.acts.reduce((m, a) => (a.ps && a.ps < m ? a.ps : m), p.acts[0]?.ps || "");
  const pe = p.acts.reduce((m, a) => (a.pe && a.pe > m ? a.pe : m), p.acts[0]?.pe || "");
  return { plan, act, achieve: plan ? Math.round((act / plan) * 100) : 0, status, ps, pe };
}